import { ref, reactive } from 'vue';
import axios from 'axios';

axios.defaults.withCredentials = true;

// Estado global de autenticación
const user = reactive({
  email: '',
  name: '',
  permissions: [],
  profilePicture: '',
  subscription: {}
});

const isAuthenticated = ref(false);
const isLoading = ref(false);

export function useAuth() {
  
  const checkAuth = async (showToast = false) => {
    isLoading.value = true;
    try {
      const response = await axios.get(process.env.VUE_APP_BASE_URL + '/api/v2/users/me/');
      const { email, name, permissions, picture, subscription } = response.data;
      
      // Actualizar estado del usuario
      user.email = email;
      user.name = name;
      user.permissions = permissions;
      user.profilePicture = picture;
      user.subscription = subscription;
      
      isAuthenticated.value = true;
      
      if (showToast && window.showToast) {
        window.showToast('¡Bienvenido/a ' + name + '!', 'success');
      }
      
      return true;
    } catch (error) {
      // Limpiar estado si no está autenticado
      clearUserData();
      
      if (showToast && window.showToast) {
        console.error('User data request failed:', error);
        window.showToast('Error al obtener tus datos. Por favor, intentá de nuevo.', 'error');
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const login = async (userData) => {
    const { email, name, permissions, profilePicture, subscription } = userData;
    
    user.email = email;
    user.name = name;
    user.permissions = permissions;
    user.profilePicture = profilePicture;
    user.subscription = subscription;
    
    isAuthenticated.value = true;
    
    // Guardar en localStorage para persistencia
    localStorage.setItem('user_email', email);
    localStorage.setItem('user_name', name);
    localStorage.setItem('user_permissions', JSON.stringify(permissions));
    localStorage.setItem('user_profile_picture', profilePicture);
    localStorage.setItem('user_subscription', JSON.stringify(subscription));
  };

  const logout = async (router = null, showToast = null) => {
    try {
      await axios.post(process.env.VUE_APP_BASE_URL + '/api/auth/logout/');
      
      if (showToast) {
        showToast('Sesión cerrada. ¡Hasta la próxima!', 'success');
      }
    } catch (error) {
      console.error('Error logging out:', error);
      
      if (showToast) {
        showToast('¡Estamos con mucha demanda! al intentar cerrar sesión.', 'error');
      }
    } finally {
      // Limpiar estado local independientemente del resultado
      clearUserData();
      
      // Redirigir al login
      if (router) {
        router.push('/login');
      }
    }
  };

  const clearUserData = () => {
    user.email = '';
    user.name = '';
    user.permissions = [];
    user.profilePicture = '';
    user.subscription = {};
    
    isAuthenticated.value = false;
    
    // Limpiar localStorage
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('user_profile_picture');
    localStorage.removeItem('user_subscription');
  };

  const restoreFromLocalStorage = () => {
    const email = localStorage.getItem('user_email');
    const name = localStorage.getItem('user_name');
    const permissions = localStorage.getItem('user_permissions');
    const profilePicture = localStorage.getItem('user_profile_picture');
    const subscription = localStorage.getItem('user_subscription');

    if (email && name) {
      user.email = email;
      user.name = name;
      user.permissions = permissions ? JSON.parse(permissions) : [];
      user.profilePicture = profilePicture || '';
      user.subscription = subscription ? JSON.parse(subscription) : {};
      
      isAuthenticated.value = true;
      return true;
    }
    
    return false;
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    checkAuth,
    login,
    logout,
    clearUserData,
    restoreFromLocalStorage
  };
}
